/**
 * Remember the customer's Size selection across PDP navigations and
 * combined-listing product switches. Stores the option *label* (e.g. "M"),
 * not variant / option_value ids (those differ per product).
 *
 * sessionStorage — lasts the tab session only.
 * Skips initial restore when ?variant= is present (deep link wins).
 */
const STORAGE_KEY = 'modaest.lastSize.v1';
const SIZE_OPTION = 'size';

/**
 * @returns {string | null}
 */
export function readRememberedSize() {
  try {
    const value = sessionStorage.getItem(STORAGE_KEY);
    return value && value.trim() !== '' ? value : null;
  } catch {
    return null;
  }
}

/**
 * @param {string} value
 */
export function writeRememberedSize(value) {
  if (typeof value !== 'string' || value.trim() === '') return;
  try {
    sessionStorage.setItem(STORAGE_KEY, value.trim());
  } catch {
    // Private mode / quota — ignore.
  }
}

/**
 * @param {Element} root
 * @returns {HTMLInputElement[]}
 */
function sizeRadios(root) {
  return [...root.querySelectorAll('fieldset input[type="radio"][data-option-name]')].filter(
    (el) =>
      el instanceof HTMLInputElement &&
      (el.getAttribute('data-option-name') || '').trim().toLowerCase() === SIZE_OPTION
  );
}

/**
 * PDP variant pickers only (not product cards / quick-add).
 * @returns {HTMLElement[]}
 */
function pdpPickers() {
  return [...document.querySelectorAll('variant-picker')].filter(
    (el) =>
      el instanceof HTMLElement &&
      !el.closest('product-card') &&
      !el.closest('quick-add-dialog') &&
      !el.closest('swatches-variant-picker-component')
  );
}

/**
 * @param {{ force?: boolean }} [opts]
 */
export function restoreRememberedSize(opts = {}) {
  const saved = readRememberedSize();
  if (!saved) return;

  const force = opts.force === true;
  if (!force) {
    try {
      if (new URLSearchParams(window.location.search).has('variant')) return;
    } catch {
      // ignore
    }
  }

  for (const picker of pdpPickers()) {
    const radios = sizeRadios(picker);
    if (radios.length === 0) continue;

    const current = radios.find((r) => r.checked);
    if (current && current.value === saved) continue;

    const target = radios.find(
      (r) => r.value === saved && r.getAttribute('data-option-available') !== 'false' && !r.disabled
    );
    if (!target) continue;

    // Fabric listens to change on the picker; click mirrors customer input.
    target.click();
  }
}

function onSizeChange(event) {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;
  if (target.type !== 'radio') return;
  if ((target.getAttribute('data-option-name') || '').trim().toLowerCase() !== SIZE_OPTION) return;
  if (!target.checked) return;
  if (target.closest('product-card') || target.closest('quick-add-dialog')) return;
  writeRememberedSize(target.value);
}

let restoreTimer = 0;
function scheduleRestore(force = false) {
  window.clearTimeout(restoreTimer);
  restoreTimer = window.setTimeout(() => restoreRememberedSize({ force }), 50);
}

function onProductSelect(event) {
  // After combined-listing morph, option_value ids change — re-apply by label.
  const detail = /** @type {{ promise?: Promise<unknown> }} */ (event)?.detail;
  const promise = detail?.promise;
  if (promise && typeof promise.then === 'function') {
    promise.then(() => scheduleRestore(true)).catch(() => scheduleRestore(true));
    return;
  }
  scheduleRestore(true);
}

function init() {
  document.addEventListener('change', onSizeChange, true);
  document.addEventListener('shopify:product:select', onProductSelect);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scheduleRestore(false), { once: true });
  } else {
    scheduleRestore(false);
  }
}

init();
